import { getDeviceId, getSessionId } from '@amplitude/analytics-browser';
import capitalize from 'lodash/capitalize';
import { useRouter } from 'next/navigation';
import qs from 'query-string';
import { useCallback, useRef } from 'react';

import type { SubscriptionsEligibilityValidationResponse } from '@calm-web/api-sdk/lib/atlas-subscriptions-api';
import type { GetV1UsersUserIdPrivacyFlagsResponseBody } from '@calm-web/api-sdk/lib/internal-sleep-api';

import { AuthPrompt, useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useIsAuthenticating } from '@/context/auth/IsAuthenticatingProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useSetupSleepUserAfterAuth as useSetupUserAfterAuth } from '@/hooks/forms/useSetupUserAfterAuth';
import { useGuestPassRequestCode } from '@/hooks/guestPass/useGuestPassRequestCode';
import {
	useComplianceLevelState,
	usePartnerState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import { IS_GDPR_LIMITED, REQUIRE_GDPR_MODAL } from '@/store/complianceLevel/types';
import type { Subscription, User } from '@/store/user/types';
import { getCouponForLogging, getSlug } from '@/utils/analytics/setupEventProps';
import { calmLogger } from '@/utils/calmLogger';
import getUrl from '@/utils/getUrl';
import { connectionByAuthMethod } from '@/utils/privacyCookies';

import { BroadcastChannelEvent, BroadcastChannelName } from './enums';
import type { SignupArgs, UseAuth } from './types';

export type UseSleepAuthInternalProps = {
	authPrompt: AuthPrompt;
	offerCode?: string;
	partnershipId?: string;
	sku?: string;
};

export const useSleepAuthInternal = ({
	authPrompt,
	offerCode,
	partnershipId,
	sku,
}: UseSleepAuthInternalProps): UseAuth => {
	const user = useUserState();

	const apiRequest = useApi();

	const setupUserAfterAuth = useSetupUserAfterAuth();
	const { setIsAuthenticating } = useIsAuthenticating();

	const signupPopup = useRef<Window | null>(null);
	const { logEvent } = useAnalytics();
	const guestPassCode = useGuestPassRequestCode();
	const partner = usePartnerState();
	const purchaseParamsState = usePurchaseParamsState();
	const router = useRouter();

	const complianceLevel = useComplianceLevelState();

	const authenticate = useCallback(
		({ authMethod, marketingOptIn, onAuthSuccess, loginHint }: SignupArgs) => {
			logEvent({
				eventName: `Login Form : ${capitalize(authMethod)} : Clicked`,
				eventProps: {
					mode: authPrompt,
				},
			});
			setIsAuthenticating(true);

			const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
			channel.onmessage = async function (event) {
				const { type } = event.data;

				if (
					type === BroadcastChannelEvent.LOGIN_COMPLETED ||
					type === BroadcastChannelEvent.SIGNUP_COMPLETED
				) {
					// Close channel, so that we avoid catching any subsequent events (WINDOW_CLOSED in particular).
					channel.close();

					const { data: sessionUser } = await apiRequest<{
						'https://oneid.calm.com/calm_oneid'?: string;
						email?: string;
						given_name?: string;
						'https://oneid.calm.com/given_name'?: string;
					}>({
						service: 'calm-sleep-app',
						endpoint: 'auth/profile',
						method: 'GET',
					});

					const sleepUser: User = {
						id: sessionUser['https://oneid.calm.com/calm_oneid'],
						email: sessionUser.email,
						name: sessionUser.given_name ?? sessionUser['https://oneid.calm.com/given_name'],
						current_auth_type: 'auth0',
					};

					if (type === BroadcastChannelEvent.SIGNUP_COMPLETED) {
						const requiresGdprConsent =
							complianceLevel === REQUIRE_GDPR_MODAL || complianceLevel === IS_GDPR_LIMITED;
						if (requiresGdprConsent) {
							try {
								await apiRequest({
									service: 'calm-sleep-app-web-api',
									endpoint: 'lifecycle-marketing-communication',
									method: 'POST',
									body: {
										marketing_opt_in: marketingOptIn,
									},
								});
							} catch (error) {
								// the error is logged in the API call
							}
						}
					}

					try {
						const {
							data: { ldu_opt_in_at },
						} = await apiRequest<GetV1UsersUserIdPrivacyFlagsResponseBody>({
							service: 'calm-sleep-app-web-api',
							endpoint: 'ldu-opt-in-at',
							method: 'GET',
						});
						sleepUser.has_opted_in_limited_data_use = typeof ldu_opt_in_at === 'string';
					} catch (error) {
						// for local development, since we can't send requests to internal APIs
						if (process.env.NEXT_PUBLIC_CALM_ENV === 'local') {
							sleepUser.has_opted_in_limited_data_use = false;
						}
					}

					try {
						if (!sku) {
							throw new Error();
						}
						const {
							data: { status },
						} = await apiRequest<SubscriptionsEligibilityValidationResponse>({
							service: 'calm-sleep-app-web-api',
							endpoint: 'eligibility-status',
							method: 'POST',
							body: {
								sku,
								offerCode,
								partnershipId,
							},
						});
						sleepUser.eligibility_validation_status = status;
					} catch (error) {
						calmLogger.error('Error in useAuth authenticateSleep sleep/eligibility-status', {}, error);
					}
					if (sleepUser.eligibility_validation_status === 'already-subscribed') {
						sleepUser.subscription = { valid: true } as Subscription;
					}

					const isSignup = type === BroadcastChannelEvent.SIGNUP_COMPLETED;
					const mode = isSignup ? 'signup' : 'login';

					try {
						await setupUserAfterAuth(sleepUser, authMethod);
						const authSuccess = onAuthSuccess?.(sleepUser);
						router.refresh();

						// Existing event for backward compatibility
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Success`,
							eventProps: {
								mode,
							},
						});

						// Granular auth success events
						logEvent({
							eventName: isSignup ? 'Account : Signup : Success' : 'Account : Login : Success',
							eventProps: {
								auth_method: authMethod,
								...(!!sleepUser?.id && { calm_user_id: sleepUser.id }),
							},
						});

						// Unified auth success event (regardless of signup or login)
						logEvent({
							eventName: 'Account : Auth : Succeeded',
							eventProps: {
								auth_method: authMethod,
								mode,
								...(!!sleepUser?.id && { calm_user_id: sleepUser.id }),
							},
						});

						if (isSignup) {
							logEvent({
								eventName: 'Account : User : Created',
								eventProps: {
									...(!!sleepUser?.id && { calm_user_id: sleepUser.id }),
								},
							});
						}
						return authSuccess;
					} catch (error) {
						// Existing event for backward compatibility
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Error`,
							eventProps: {
								mode,
							},
						});

						// Granular auth failure events
						logEvent({
							eventName: isSignup ? 'Account : Signup : Failed' : 'Account : Login : Failed',
							eventProps: {
								auth_method: authMethod,
								...(!!sleepUser?.id && { calm_user_id: sleepUser.id }),
							},
						});
					} finally {
						setIsAuthenticating(false);
					}
				}

				if (type === BroadcastChannelEvent.WINDOW_CLOSED) {
					logEvent({
						eventName: `Login Form : ${capitalize(authMethod)} : Cancelled`,
						eventProps: {
							mode: authPrompt,
						},
					});
					channel.close();
					setIsAuthenticating(false);
				}
			};

			if (signupPopup.current) {
				signupPopup.current.focus();
			} else {
				const left = (window.innerWidth - 600) / 2 + window.screenX;
				const top = (window.innerHeight - 600) / 2 + window.screenY;
				signupPopup.current = window.open(
					`${getUrl('calm-sleep-app')}/auth/login?${qs.stringify({
						connection: connectionByAuthMethod[authMethod],
						screen_hint: authPrompt,

						marketing_opt_in: marketingOptIn,
						requires_gdpr_consent:
							complianceLevel === REQUIRE_GDPR_MODAL || complianceLevel === IS_GDPR_LIMITED,
						guest_pass_code: guestPassCode,
						partner_id: partner?.id,
						partner_type: [partner?.category?.toLowerCase() || null],
						slug: getSlug(),
						coupon: getCouponForLogging(purchaseParamsState?.coupon),
						login_hint: loginHint,
						// send the device id to universal login page to connect the device with the user
						['ext-amp_device_id']: getDeviceId(),
						['ext-amp_session_id']: getSessionId(),
					})}`,
					'_blank',
					`popup,width=600,height=600,top=${top},left=${left}`,
				);

				if (signupPopup.current) {
					const interval = setInterval(() => {
						if (signupPopup.current?.closed) {
							signupPopup.current = null;
							clearInterval(interval);
							const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
							channel.postMessage({ type: BroadcastChannelEvent.WINDOW_CLOSED });
						}
					}, 500);
				}
			}
		},
		[
			apiRequest,
			authPrompt,
			guestPassCode,
			logEvent,
			partner?.category,
			partner?.id,
			purchaseParamsState?.coupon,
			sku,
			offerCode,
			partnershipId,
			setIsAuthenticating,
			setupUserAfterAuth,
			complianceLevel,
			router,
		],
	);

	return {
		authenticate,
		isAuth0User: user?.current_auth_type === 'auth0',
		isLoading: false, // No experiment loading state needed for Sleep
	};
};

export default function useSleepAuth() {
	const { authPrompt } = useAuthPrompt();
	const { offerCode, partnershipId, sku } = usePurchaseParamsState() ?? {};
	return useSleepAuthInternal({ authPrompt, offerCode, partnershipId, sku });
}
